import React from 'react';
import {
  LineChart,
  Line,
  BarChart,
  Bar,
  PieChart,
  Pie,
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  Cell,
  RadarChart,
  PolarGrid,
  PolarAngleAxis,
  PolarRadiusAxis,
  Radar,
  ScatterChart,
  Scatter,
  ComposedChart,
  ScatterLine,
  AreaClosed,
  LineClosed
} from 'recharts';

// Recharts Line Chart
interface RechartsLineChartProps {
  data: Array<Record<string, any>>;
  dataKey?: string;
  xAxisDataKey?: string;
  width?: number | string;
  height?: number;
  colors?: string[];
  showGrid?: boolean;
  showLegend?: boolean;
  showTooltip?: boolean;
  className?: string;
}

export const RechartsLineChart: React.FC<RechartsLineChartProps> = ({
  data,
  dataKey = 'value',
  xAxisDataKey = 'name',
  width = '100%',
  height = 400,
  colors = ['#3b82f6', '#10b981', '#f59e0b', '#ef4444', '#8b5cf6'],
  showGrid = true,
  showLegend = true,
  showTooltip = true,
  className = ''
}) => {
  const dataKeys = Object.keys(data[0] || {}).filter(key => key !== xAxisDataKey);

  return (
    <div className={className}>
      <ResponsiveContainer width={width} height={height}>
        <LineChart data={data}>
          {showGrid && <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />}
          <XAxis dataKey={xAxisDataKey} stroke="#6b7280" fontSize={12} />
          <YAxis stroke="#6b7280" fontSize={12} />
          {showTooltip && <Tooltip />}
          {showLegend && <Legend />}
          {dataKeys.map((key, index) => (
            <Line
              key={key}
              type="monotone"
              dataKey={key}
              stroke={colors[index % colors.length]}
              strokeWidth={2}
              dot={{ r: 4 }}
              activeDot={{ r: 6 }}
            />
          ))}
        </LineChart>
      </ResponsiveContainer>
    </div>
  );
};

// Recharts Area Chart
interface RechartsAreaChartProps {
  data: Array<Record<string, any>>;
  xAxisDataKey?: string;
  width?: number | string;
  height?: number;
  colors?: string[];
  showGrid?: boolean;
  showLegend?: boolean;
  showTooltip?: boolean;
  stacked?: boolean;
  className?: string;
}

export const RechartsAreaChart: React.FC<RechartsAreaChartProps> = ({
  data,
  xAxisDataKey = 'name',
  width = '100%',
  height = 400,
  colors = ['#3b82f6', '#10b981', '#f59e0b', '#ef4444', '#8b5cf6'],
  showGrid = true,
  showLegend = true,
  showTooltip = true,
  stacked = false,
  className = ''
}) => {
  const dataKeys = Object.keys(data[0] || {}).filter(key => key !== xAxisDataKey);

  return (
    <div className={className}>
      <ResponsiveContainer width={width} height={height}>
        <AreaChart data={data}>
          {showGrid && <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />}
          <XAxis dataKey={xAxisDataKey} stroke="#6b7280" fontSize={12} />
          <YAxis stroke="#6b7280" fontSize={12} />
          {showTooltip && <Tooltip />}
          {showLegend && <Legend />}
          {dataKeys.map((key, index) => (
            <Area
              key={key}
              type="monotone"
              dataKey={key}
              stackId={stacked ? '1' : undefined}
              stroke={colors[index % colors.length]}
              fill={colors[index % colors.length]}
              fillOpacity={0.6}
            />
          ))}
        </AreaChart>
      </ResponsiveContainer>
    </div>
  );
};

// Recharts Bar Chart
interface RechartsBarChartProps {
  data: Array<Record<string, any>>;
  xAxisDataKey?: string;
  width?: number | string;
  height?: number;
  colors?: string[];
  showGrid?: boolean;
  showLegend?: boolean;
  showTooltip?: boolean;
  horizontal?: boolean;
  stacked?: boolean;
  className?: string;
}

export const RechartsBarChart: React.FC<RechartsBarChartProps> = ({
  data,
  xAxisDataKey = 'name',
  width = '100%',
  height = 400,
  colors = ['#3b82f6', '#10b981', '#f59e0b', '#ef4444', '#8b5cf6'],
  showGrid = true,
  showLegend = true,
  showTooltip = true,
  horizontal = false,
  stacked = false,
  className = ''
}) => {
  const dataKeys = Object.keys(data[0] || {}).filter(key => key !== xAxisDataKey);

  return (
    <div className={className}>
      <ResponsiveContainer width={width} height={height}>
        <BarChart data={data} layout={horizontal ? 'vertical' : 'horizontal'}>
          {showGrid && <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />}
          {horizontal ? (
            <>
              <XAxis type="number" stroke="#6b7280" fontSize={12} />
              <YAxis dataKey={xAxisDataKey} type="category" stroke="#6b7280" fontSize={12} />
            </>
          ) : (
            <>
              <XAxis dataKey={xAxisDataKey} stroke="#6b7280" fontSize={12} />
              <YAxis stroke="#6b7280" fontSize={12} />
            </>
          )}
          {showTooltip && <Tooltip />}
          {showLegend && <Legend />}
          {dataKeys.map((key, index) => (
            <Bar
              key={key}
              dataKey={key}
              stackId={stacked ? '1' : undefined}
              fill={colors[index % colors.length]}
              radius={horizontal ? [0, 4, 4, 0] : [4, 4, 0, 0]}
            />
          ))}
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
};

// Recharts Pie Chart
interface RechartsPieChartProps {
  data: Array<{ name: string; value: number; color?: string }>;
  width?: number | string;
  height?: number;
  innerRadius?: number;
  outerRadius?: number;
  showLabels?: boolean;
  showLegend?: boolean;
  className?: string;
}

export const RechartsPieChart: React.FC<RechartsPieChartProps> = ({
  data,
  width = '100%',
  height = 400,
  innerRadius = 0,
  outerRadius = 150,
  showLabels = true,
  showLegend = true,
  className = ''
}) => {
  const defaultColors = ['#3b82f6', '#10b981', '#f59e0b', '#ef4444', '#8b5cf6', '#ec4899'];

  return (
    <div className={className}>
      <ResponsiveContainer width={width} height={height}>
        <PieChart>
          <Pie
            data={data}
            cx="50%"
            cy="50%"
            innerRadius={innerRadius}
            outerRadius={outerRadius}
            paddingAngle={2}
            dataKey="value"
            label={showLabels ? ({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%` : undefined}
          >
            {data.map((entry, index) => (
              <Cell key={`cell-${index}`} fill={entry.color || defaultColors[index % defaultColors.length]} />
            ))}
          </Pie>
          {showTooltip && <Tooltip />}
          {showLegend && <Legend />}
        </PieChart>
      </ResponsiveContainer>
    </div>
  );
};

// Recharts Donut Chart
interface RechartsDonutChartProps {
  data: Array<{ name: string; value: number; color?: string }>;
  width?: number | string;
  height?: number;
  innerRadius?: number;
  outerRadius?: number;
  centerLabel?: string;
  centerValue?: string | number;
  className?: string;
}

export const RechartsDonutChart: React.FC<RechartsDonutChartProps> = ({
  data,
  width = '100%',
  height = 400,
  innerRadius = 60,
  outerRadius = 100,
  centerLabel,
  centerValue,
  className = ''
}) => {
  const defaultColors = ['#3b82f6', '#10b981', '#f59e0b', '#ef4444', '#8b5cf6', '#ec4899'];

  return (
    <div className={`relative ${className}`}>
      <ResponsiveContainer width={width} height={height}>
        <PieChart>
          <Pie
            data={data}
            cx="50%"
            cy="50%"
            innerRadius={innerRadius}
            outerRadius={outerRadius}
            paddingAngle={2}
            dataKey="value"
          >
            {data.map((entry, index) => (
              <Cell key={`cell-${index}`} fill={entry.color || defaultColors[index % defaultColors.length]} />
            ))}
          </Pie>
          <Tooltip />
          <Legend />
        </PieChart>
      </ResponsiveContainer>
      {(centerLabel || centerValue) && (
        <div className="absolute inset-0 flex flex-col items-center justify-center pointer-events-none">
          {centerValue && <span className="text-2xl font-bold text-khiimori-text-primary">{centerValue}</span>}
          {centerLabel && <span className="text-sm text-khiimori-text-secondary">{centerLabel}</span>}
        </div>
      )}
    </div>
  );
};

// Recharts Radar Chart
interface RechartsRadarChartProps {
  data: Array<Record<string, any>>;
  dataKeys?: string[];
  width?: number | string;
  height?: number;
  colors?: string[];
  className?: string;
}

export const RechartsRadarChart: React.FC<RechartsRadarChartProps> = ({
  data,
  dataKeys,
  width = '100%',
  height = 400,
  colors = ['#3b82f6', '#10b981', '#f59e0b'],
  className = ''
}) => {
  const defaultKeys = dataKeys || Object.keys(data[0] || {}).slice(1);

  return (
    <div className={className}>
      <ResponsiveContainer width={width} height={height}>
        <RadarChart data={data}>
          <PolarGrid stroke="#e5e7eb" />
          <PolarAngleAxis dataKey={Object.keys(data[0] || {})[0]} stroke="#6b7280" fontSize={12} />
          <PolarRadiusAxis stroke="#6b7280" fontSize={10} />
          {defaultKeys.map((key, index) => (
            <Radar
              key={key}
              name={key}
              dataKey={key}
              stroke={colors[index % colors.length]}
              fill={colors[index % colors.length]}
              fillOpacity={0.3}
            />
          ))}
          <Legend />
          <Tooltip />
        </RadarChart>
      </ResponsiveContainer>
    </div>
  );
};

// Recharts Scatter Chart
interface RechartsScatterChartProps {
  data: Array<{ x: number; y: number; z?: number }>;
  width?: number | string;
  height?: number;
  color?: string;
  showGrid?: boolean;
  showTooltip?: boolean;
  className?: string;
}

export const RechartsScatterChart: React.FC<RechartsScatterChartProps> = ({
  data,
  width = '100%',
  height = 400,
  color = '#3b82f6',
  showGrid = true,
  showTooltip = true,
  className = ''
}) => {
  return (
    <div className={className}>
      <ResponsiveContainer width={width} height={height}>
        <ScatterChart>
          {showGrid && <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />}
          <XAxis dataKey="x" name="X" stroke="#6b7280" fontSize={12} />
          <YAxis dataKey="y" name="Y" stroke="#6b7280" fontSize={12} />
          {showTooltip && <Tooltip cursor={{ strokeDasharray: '3 3' }} />}
          <Scatter name="Data" data={data} fill={color} />
        </ScatterChart>
      </ResponsiveContainer>
    </div>
  );
};

// Recharts Composed Chart
interface RechartsComposedChartProps {
  data: Array<Record<string, any>>;
  xAxisDataKey?: string;
  width?: number | string;
  height?: number;
  colors?: string[];
  className?: string;
}

export const RechartsComposedChart: React.FC<RechartsComposedChartProps> = ({
  data,
  xAxisDataKey = 'name',
  width = '100%',
  height = 400,
  colors = ['#3b82f6', '#10b981', '#f59e0b'],
  className = ''
}) => {
  const dataKeys = Object.keys(data[0] || {}).filter(key => key !== xAxisDataKey);

  return (
    <div className={className}>
      <ResponsiveContainer width={width} height={height}>
        <ComposedChart data={data}>
          <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
          <XAxis dataKey={xAxisDataKey} stroke="#6b7280" fontSize={12} />
          <YAxis stroke="#6b7280" fontSize={12} />
          <Tooltip />
          <Legend />
          {dataKeys.map((key, index) => {
            if (index % 2 === 0) {
              return (
                <Bar
                  key={key}
                  dataKey={key}
                  fill={colors[index % colors.length]}
                  radius={[4, 4, 0, 0]}
                />
              );
            }
            return (
              <Line
                key={key}
                type="monotone"
                dataKey={key}
                stroke={colors[index % colors.length]}
                strokeWidth={2}
              />
            );
          })}
        </ComposedChart>
      </ResponsiveContainer>
    </div>
  );
};

// Recharts Progress Bar Chart
interface RechartsProgressChartProps {
  data: Array<{ name: string; value: number; max?: number }>;
  width?: number | string;
  height?: number;
  color?: string;
  className?: string;
}

export const RechartsProgressChart: React.FC<RechartsProgressChartProps> = ({
  data,
  width = '100%',
  height = 400,
  color = '#3b82f6',
  className = ''
}) => {
  return (
    <div className={className}>
      <ResponsiveContainer width={width} height={height}>
        <BarChart data={data} layout="vertical">
          <XAxis type="number" domain={[0, 100]} stroke="#6b7280" fontSize={12} />
          <YAxis dataKey="name" type="category" stroke="#6b7280" fontSize={12} width={80} />
          <Tooltip />
          <Bar dataKey="value" fill={color} radius={[0, 4, 4, 0]} />
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
};

// Recharts Funnel Chart (using custom approach with Bar chart)
interface RechartsFunnelChartProps {
  data: Array<{ name: string; value: number }>;
  width?: number | string;
  height?: number;
  colors?: string[];
  className?: string;
}

export const RechartsFunnelChart: React.FC<RechartsFunnelChartProps> = ({
  data,
  width = '100%',
  height = 400,
  colors = ['#3b82f6', '#10b981', '#f59e0b', '#ef4444', '#8b5cf6'],
  className = ''
}) => {
  return (
    <div className={className}>
      <ResponsiveContainer width={width} height={height}>
        <BarChart data={data} layout="vertical">
          <XAxis type="number" stroke="#6b7280" fontSize={12} />
          <YAxis dataKey="name" type="category" stroke="#6b7280" fontSize={12} width={100} />
          <Tooltip />
          <Bar dataKey="value" radius={[0, 4, 4, 0]}>
            {data.map((entry, index) => (
              <Cell key={`cell-${index}`} fill={colors[index % colors.length]} />
            ))}
          </Bar>
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
};

export default {
  RechartsLineChart,
  RechartsAreaChart,
  RechartsBarChart,
  RechartsPieChart,
  RechartsDonutChart,
  RechartsRadarChart,
  RechartsScatterChart,
  RechartsComposedChart,
  RechartsProgressChart,
  RechartsFunnelChart
};